const CUSTOM_FIELD_SUFFIX = '_c'
const CUSTOM_FIELD_REGEXP = new RegExp(CUSTOM_FIELD_SUFFIX + '$', 'gi')

export function addSuffix(input: string) {
    return input + CUSTOM_FIELD_SUFFIX
}

export function getCustomFieldName(input: string, withoutSuffix = false) {
    const name = trimAllUnderscoreC(input).trim().toLowerCase()

    if (name.length === 0) {
        return false
    }

    return withoutSuffix ? name : addSuffix(name)
}

export function trimAllUnderscoreC(input: string) {
    let name = input
    let replace = trimUnderscoreC(name)

    while (name !== replace) {
        name = replace
        replace = trimUnderscoreC(name)
    }

    return name
}

export function trimUnderscoreC(input: string) {
    return input.replace(CUSTOM_FIELD_REGEXP, '')
}

export function isExisting(
    existCustomFields: string[],
    newCustomField: string
) {
    const name = getCustomFieldName(newCustomField)

    if (name === false) {
        return false
    }

    return existCustomFields.indexOf(name) > -1
}

export function findAllIndexesInString(str: string, delim: string) {
    let index = -1
    const indexes: number[] = []
    while (-1 !== (index = str.indexOf(delim, index + 1))) {
        indexes.push(index)
    }
    return indexes
}

export interface FieldInterpolation {
    start: number
    end: number
    open: boolean
    incomplete: boolean
    padding?: string
    field?: string
    fallback?: string
}

export const initialFieldInterpolation: FieldInterpolation = {
    start: 0,
    end: 0,
    open: false,
    incomplete: false,
    padding: '',
    field: '',
    fallback: '',
}

/**
 * Extracts (padding)(field)(fallback exists?)(fallback)
 * @param fieldAndFallback
 */
export function getFieldFallback(fieldAndFallback) {
    return fieldAndFallback.match(/^( *)([^ {}\n]*)( fallback )?([^{}]*)/)
}

/**
 * Parses a string into custom fields of format:
 * - {field}
 * - {field
 * - {field fallback text}
 * - {field fallback text
 *
 * Returning details:
 *
 * - start: where the field starts
 * - end: where the field ends
 * - open: whether this is the start or end of a field
 * - incomplete: whether this field is completed or not
 * - padding: spaces to be inserted before the field
 * - field
 * - fallback: fallback text if field doesn't exist
 *
 * @param str
 * @param openDelim
 * @param closeDelim
 * @param filter
 */
export function getFieldInterpolations(
    str,
    openDelim = '{',
    closeDelim = '}',
    filter = ({ open }) => open
) {
    const openDelims = findAllIndexesInString(str, openDelim)
    const closeDelims = findAllIndexesInString(str, closeDelim)

    let openIndex = 0
    let closeIndex = 0
    const sequence: FieldInterpolation[] = []

    /**
     * sort through open and close delimiters to parse out potential fields
     */
    while (openIndex < openDelims.length || closeIndex < closeDelims.length) {
        const openAvailable = openIndex !== openDelims.length
        const closeAvailable = closeIndex !== closeDelims.length

        if (openAvailable) {
            if (
                !closeAvailable ||
                openDelims[openIndex] < closeDelims[closeIndex] // next occurrence is an open delimiter
            ) {
                sequence.push({
                    start: openDelims[openIndex],
                    end: -1,
                    open: true,
                    incomplete: false,
                })
                openIndex++
            }
        }

        if (closeAvailable) {
            if (
                !openAvailable ||
                openDelims[openIndex] > closeDelims[closeIndex] // next occurrence is a close delimiter
            ) {
                sequence.push({
                    start: closeDelims[closeIndex],
                    end: -1,
                    open: false,
                    incomplete: false,
                })
                closeIndex++
            }
        }
    }

    /**
     * iterate over sorted delimiters in the sequence to parse field names and fallbacks
     */
    for (let i = 0; i < sequence.length; i++) {
        if (!sequence[i].open) {
            continue
        }
        const atEnd = i === sequence.length - 1
        const endIndex = atEnd ? str.length : sequence[i + 1].start

        const rawField = str.substring(sequence[i].start + 1, endIndex)
        const [, padding, field, fallback, fallbackValue] =
            getFieldFallback(rawField)

        sequence[i].field = field
        sequence[i].padding = padding

        // if the next delimiter in the sequence is also an open delimiter, this field is incomplete
        if (atEnd || sequence[i + 1].open) {
            sequence[i].incomplete = true
            sequence[i].end =
                sequence[i].start + 1 + padding.length + field.length
        } else {
            sequence[i].end = endIndex
        }

        if (fallback) {
            sequence[i].fallback = fallbackValue
            sequence[i].end += 10 + fallbackValue.length
        }
    }
    return sequence.filter(filter)
}
